import React, { RefObject } from 'react'
import { Form, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { FormikProps } from 'formik'
import {
  ARBITRATION_MAX_DOCUMENTS,
  ArbitrationIssueType,
  IArbitrationFormValues,
  IArbitrationMetadata
} from 'types/arbitrationTypes'
import { StyledExistingDocuments, StyledFormSection, StyledReadOnlyGrid } from '../ArbitrationForm.styled'
import { FormFieldInput } from '../FormFieldInput'
import { FormFieldLabel } from '../FormFieldLabel'
import {
  StyledArbitrationRequestSection,
  StyledDocumentRequirement,
  StyledMileageWarning
} from './ArbitrationRequestSection.styled'
import { DragDropFileUploader } from 'components/FileUploader/DragDropFileUploader'

interface IProps {
  metadata: IArbitrationMetadata
  formik: FormikProps<IArbitrationFormValues>
  isAddAttachmentsMode: boolean
  existingDocuments: Array<{ id: number; fileName: string }>
  attachmentAcceptValue: string
  isDragActive: boolean
  selectedIssueType: number
  issueTypeHint: string | null
  isMileageBelowSale: boolean
  exceedsMileage: boolean
  mileageBelowWarning: string
  mileageExceedWarning: string
  reasonMaxLength: number
  emptyUploaderText: string
  fileInputRef: RefObject<HTMLInputElement>
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>
  onDrop: (event: React.DragEvent<HTMLDivElement>) => Promise<void>
  onDragOver: (event: React.DragEvent<HTMLDivElement>) => void
  onDragLeave: (event: React.DragEvent<HTMLDivElement>) => void
  onRemoveDocument: (index: number) => void
}

export const ArbitrationRequestSection: React.FC<IProps> = ({
  metadata,
  formik,
  isAddAttachmentsMode,
  existingDocuments,
  attachmentAcceptValue,
  isDragActive,
  selectedIssueType,
  issueTypeHint,
  isMileageBelowSale,
  exceedsMileage,
  mileageBelowWarning,
  mileageExceedWarning,
  reasonMaxLength,
  emptyUploaderText,
  fileInputRef,
  onFileChange,
  onDrop,
  onDragOver,
  onDragLeave,
  onRemoveDocument
}) => {
  // Like BMW: compute slot limit inside the section, not in the parent
  const configuredMaxDocuments = metadata.maxDocumentCount ?? ARBITRATION_MAX_DOCUMENTS
  const effectiveMax = isAddAttachmentsMode
    ? Math.max(0, configuredMaxDocuments - existingDocuments.length)
    : configuredMaxDocuments
  const attachmentLimitText = `Upload ${effectiveMax} attachment${
    effectiveMax === 1 ? '' : 's'
  } as maximum, each file must not exceed ${metadata.fileSizeLimit ?? 10}MB`
  const acceptedAttachmentFormatsText = metadata.attachmentFormats?.join(', ') ?? ''

  const selectedIssueTypeDescription =
    metadata.issueTypes.find((issueType) => issueType.id === Number(formik.values.issueTypeID))?.description || 'N/A'
  const selectedResolutionTypeDescription =
    metadata.resolutionTypes.find((resolutionType) => resolutionType.id === Number(formik.values.resolutionTypeID))
      ?.description || 'N/A'

  return (
    <StyledArbitrationRequestSection>
      <StyledFormSection>
        <h6>{isAddAttachmentsMode ? 'Add Attachments' : 'Arbitration Request'}</h6>
        {isAddAttachmentsMode ? (
          <StyledReadOnlyGrid>
            <FormFieldLabel label="Issue Type" value={selectedIssueTypeDescription} />
            <FormFieldLabel label="Resolution Requested" value={selectedResolutionTypeDescription} />
            <FormFieldLabel label="Current Mileage" value={formik.values.currentMileage || 'N/A'} />
            <FormFieldLabel
              label="Reason"
              value={formik.values.reason || 'N/A'}
              fullWidth
              valueClassName="reason-value"
            />
          </StyledReadOnlyGrid>
        ) : (
          <div>
            <FormFieldInput label="Issue Type " required>
              <Form.Control
                as="select"
                name="issueTypeID"
                value={formik.values.issueTypeID}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.issueTypeID && !!formik.errors.issueTypeID}
              >
                <option value={0}>-- Select --</option>
                {metadata.issueTypes.map((issueType) => (
                  <option key={issueType.id} value={issueType.id}>
                    {issueType.description}
                  </option>
                ))}
              </Form.Control>
              <Form.Control.Feedback type="invalid">{String(formik.errors.issueTypeID || '')}</Form.Control.Feedback>
              {issueTypeHint && selectedIssueType === ArbitrationIssueType.PhysicalDamage && (
                <StyledDocumentRequirement>
                  <Form.Text className="attachment-helper-text is-priority">{issueTypeHint}</Form.Text>
                </StyledDocumentRequirement>
              )}
              {issueTypeHint && selectedIssueType !== ArbitrationIssueType.PhysicalDamage && (
                <Form.Text className="text-muted">{issueTypeHint}</Form.Text>
              )}
            </FormFieldInput>

            <FormFieldInput label="Resolution Requested " required>
              <Form.Control
                as="select"
                name="resolutionTypeID"
                value={formik.values.resolutionTypeID}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.resolutionTypeID && !!formik.errors.resolutionTypeID}
              >
                {/* <option value={0}>-- Select --</option> */}
                {metadata.resolutionTypes.map((resolutionType) => (
                  <option key={resolutionType.id} value={resolutionType.id}>
                    {resolutionType.description}
                  </option>
                ))}
              </Form.Control>
              <Form.Control.Feedback type="invalid">
                {String(formik.errors.resolutionTypeID || '')}
              </Form.Control.Feedback>
            </FormFieldInput>

            <FormFieldInput label="Current Mileage " required>
              <Form.Control
                type="text"
                name="currentMileage"
                value={formik.values.currentMileage}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.currentMileage && !!formik.errors.currentMileage}
              />
              <Form.Control.Feedback type="invalid">{String(formik.errors.currentMileage || '')}</Form.Control.Feedback>
              {isMileageBelowSale && <StyledMileageWarning>! {mileageBelowWarning}</StyledMileageWarning>}
              {exceedsMileage && <StyledMileageWarning>! {mileageExceedWarning}</StyledMileageWarning>}
            </FormFieldInput>

            <FormFieldInput label="Reason " required fullWidth>
              <Form.Control
                as="textarea"
                rows={4}
                className="reason-textarea"
                name="reason"
                maxLength={reasonMaxLength}
                value={formik.values.reason}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.reason && !!formik.errors.reason}
              />
              <Form.Text className="field-character-count reason-character-count">
                {formik.values.reason.length}/{reasonMaxLength}
              </Form.Text>
              <Form.Control.Feedback type="invalid">{formik.errors.reason}</Form.Control.Feedback>
            </FormFieldInput>
          </div>
        )}

        {isAddAttachmentsMode && existingDocuments.length > 0 && (
          <StyledExistingDocuments>
            <Form.Label>Uploaded Attachments:</Form.Label>
            <div className="documents-panel">
              <div className="documents-grid">
                {existingDocuments.map((document) => (
                  <OverlayTrigger
                    key={document.id}
                    placement="top"
                    overlay={<Tooltip id={`existing-doc-${document.id}`}>{document.fileName}</Tooltip>}
                  >
                    <div className="document-pill" title={document.fileName}>
                      {document.fileName}
                    </div>
                  </OverlayTrigger>
                ))}
              </div>
            </div>
          </StyledExistingDocuments>
        )}

        <Form.Group className="mb-2 attachment-field-group">
          <Form.Label>
            Attachments:
            {(selectedIssueType === ArbitrationIssueType.PhysicalDamage || isAddAttachmentsMode) && (
              <span className="text-danger ms-1"> *</span>
            )}
          </Form.Label>
          <div className="attachment-field-content">
            <div className="attachment-helper-panel">
              {selectedIssueType === ArbitrationIssueType.PhysicalDamage && (
                <Form.Text className="attachment-helper-text is-priority">
                  Documents required: Bill of Lading, Estimate, Photos, Transportation Invoice.
                </Form.Text>
              )}
              <Form.Text className="attachment-helper-text is-limit">{attachmentLimitText}</Form.Text>
              <Form.Text className="attachment-helper-text is-format">
                File types format allowed: {acceptedAttachmentFormatsText}
              </Form.Text>
              {isAddAttachmentsMode && effectiveMax === 0 && (
                <Form.Text className="attachment-helper-text is-disabled">
                  The maximum attachment count has already been reached for this arbitration.
                </Form.Text>
              )}
            </div>

            <DragDropFileUploader
              accept={attachmentAcceptValue}
              emptyText={emptyUploaderText}
              errorMessage={
                formik.errors.documents && formik.touched.documents ? String(formik.errors.documents || '') : undefined
              }
              fileInputRef={fileInputRef}
              isDisabled={effectiveMax === 0}
              isDragActive={isDragActive}
              selectedDocuments={formik.values.documents}
              onDragLeave={onDragLeave}
              onDragOver={onDragOver}
              onDrop={onDrop}
              onFileChange={onFileChange}
              onRemoveDocument={onRemoveDocument}
            />
          </div>
        </Form.Group>
      </StyledFormSection>
    </StyledArbitrationRequestSection>
  )
}
